import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  Paper,
  CircularProgress,
  Grid,
  Divider,
  Chip,
  Alert,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { messages } from "../../../../locales/messages";
import { GfFlyerItemCategory } from "@swagger/typescript-fetch-goflyer";
import { FrontPage } from "../../../../utils/constant/stringConstant";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";

interface ImportCategoriesModalProps {
  open: boolean;
  onClose: () => void;
  onImport: (selectedCategories: string[], file: File) => void;
  categories: GfFlyerItemCategory[];
}

export const ImportCategoriesModal: React.FC<ImportCategoriesModalProps> = ({
  open,
  onClose,
  onImport,
  categories,
}) => {
  const { t } = useTranslation();
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState<boolean>(true);
  const [displayCategories, setDisplayCategories] = useState<
    GfFlyerItemCategory[]
  >([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [dragActive, setDragActive] = useState<boolean>(false);

  useEffect(() => {
    const frontPageCategory = categories.find((cat) => cat.name === FrontPage);

    if (!frontPageCategory && categories.length > 0) {
      const frontPage = {
        id: FrontPage,
        name: FrontPage,
      } as GfFlyerItemCategory;

      const updatedCategories = [...categories, frontPage];
      setDisplayCategories(updatedCategories);

      setSelectedCategories(updatedCategories.map((cat) => cat.id as string));
      setSelectAll(true);
    } else {
      setDisplayCategories(categories);

      if (categories.length > 0) {
        setSelectedCategories(categories.map((cat) => cat.id as string));
        setSelectAll(true);
      }
    }
  }, [categories, open]);

  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategories((prev) => {
      if (prev.includes(categoryId)) {
        const newSelected = prev.filter((id) => id !== categoryId);
        setSelectAll(false);
        return newSelected;
      } else {
        const newSelected = [...prev, categoryId];
        setSelectAll(newSelected.length === displayCategories.length);
        return newSelected;
      }
    });
  };

  const handleSelectAllChange = () => {
    if (selectAll) {
      setSelectedCategories([]);
      setSelectAll(false);
    } else {
      setSelectedCategories(
        displayCategories.map((category) => category.id as string)
      );
      setSelectAll(true);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (file.name.endsWith(".xlsx") || file.name.endsWith(".xls")) {
        setSelectedFile(file);
      }
    }
  };

  const handleImport = () => {
    if (selectedFile && selectedCategories.length > 0) {
      setLoading(true);
      onImport(selectedCategories, selectedFile);

      setTimeout(() => {
        setSelectedCategories([]);
        setSelectAll(false);
        setSelectedFile(null);
        setLoading(false);
        onClose();
      }, 1000);
    }
  };

  const handleCloseModal = () => {
    setSelectedCategories([]);
    setSelectAll(false);
    setSelectedFile(null);
    setLoading(false);
    onClose();
  };

  const fileInputId = "file-upload-input";

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
      data-testid="excel-import-categories-modal"
    >
      <DialogTitle
        sx={{
          borderBottom: "1px solid #e0e0e0",
          pb: 2,
          display: "flex",
          alignItems: "center",
          backgroundColor: "#f9f9f9",
        }}
        data-testid="excel-import-categories-modal-title"
      >
        <UploadFileIcon sx={{ mr: 1, color: "#ff8038" }} />
        <Typography variant="h6" component="div" fontWeight="bold">
          {t(messages.Import_Excel())}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
              {t(messages.Select_Excel_File_And_Categories())}
            </Typography>

            <Paper
              variant="outlined"
              sx={{
                mt: 2,
                p: 3,
                border: dragActive
                  ? "2px dashed #ff8038"
                  : "2px dashed #e0e0e0",
                backgroundColor: dragActive
                  ? "rgba(255, 128, 56, 0.05)"
                  : "#f9f9f9",
                borderRadius: 2,
                transition: "all 0.3s ease",
              }}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              data-testid="excel-import-file-drop-area"
            >
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                component="label"
                htmlFor={fileInputId}
              >
                <input
                  id={fileInputId}
                  type="file"
                  onChange={handleFileChange}
                  accept=".xlsx, .xls"
                  style={{ display: "none" }}
                  data-testid="excel-import-file-input"
                />

                {selectedFile ? (
                  <Box display="flex" alignItems="center" p={2} width="100%">
                    <InsertDriveFileOutlinedIcon
                      sx={{ fontSize: 40, color: "#ff8038", mr: 2 }}
                    />
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {selectedFile.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(selectedFile.size / 1024).toFixed(2)} KB
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{
                        ml: "auto",
                        borderColor: "#ff8038",
                        color: "#ff8038",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        document.getElementById(fileInputId)?.click();
                      }}
                    >
                      {t(messages.Change_File())}
                    </Button>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      cursor: "pointer",
                      p: 3,
                    }}
                  >
                    <CloudUploadIcon
                      sx={{ fontSize: 60, color: "#ff8038", mb: 2 }}
                    />
                    <Typography variant="h6" align="center" gutterBottom>
                      {t(messages.Drop_Excel_File_Here())}
                    </Typography>
                    <Typography
                      variant="body2"
                      align="center"
                      color="text.secondary"
                      mb={2}
                    >
                      {t(messages.or_click_to_browse())}
                    </Typography>
                    <Chip
                      label=".xlsx, .xls"
                      size="small"
                      variant="outlined"
                      sx={{ borderColor: "#ff8038", color: "#ff8038" }}
                    />
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} mt={2}>
            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <FilterListIcon sx={{ mr: 1, color: "#ff8038" }} />
              <Typography variant="subtitle1" fontWeight="medium">
                {t(messages.Select_Categories_To_Import())}
              </Typography>
              <Box flexGrow={1} />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectAll}
                    onChange={handleSelectAllChange}
                    data-testid="excel-import-select-all-checkbox"
                    sx={{
                      color: "#ff8038",
                      "&.Mui-checked": {
                        color: "#ff8038",
                      },
                    }}
                  />
                }
                label={<Typography fontWeight="medium">Select_All</Typography>}
              />
            </Box>

            {displayCategories.length === 0 ? (
              <Alert
                severity="info"
                sx={{ mb: 2 }}
                data-testid="excel-import-no-categories-alert"
              >
                {t(messages.No_Categories_Available())}
              </Alert>
            ) : (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  maxHeight: "200px",
                  overflow: "auto",
                }}
              >
                <Grid
                  container
                  spacing={1}
                  data-testid="excel-import-categories-container"
                >
                  {displayCategories.map((category) => (
                    <Grid
                      item
                      xs={6}
                      sm={4}
                      key={category.id}
                      data-testid={`excel-import-category-item-category`}
                    >
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1,
                          display: "flex",
                          border: "1px solid",
                          borderColor: selectedCategories.includes(
                            category.id as string
                          )
                            ? "#ff8038"
                            : "rgba(0,0,0,0.12)",
                          borderRadius: 1,
                          bgcolor: selectedCategories.includes(
                            category.id as string
                          )
                            ? "rgba(255, 128, 56, 0.05)"
                            : "transparent",
                        }}
                        data-testid={`excel-import-category-item-${category.id}`}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedCategories.includes(
                                category.id as string
                              )}
                              onChange={() =>
                                handleCategoryChange(category.id as string)
                              }
                              data-testid={`excel-import-category-checkbox-${category.id}`}
                              sx={{
                                color: "#ff8038",
                                "&.Mui-checked": {
                                  color: "#ff8038",
                                },
                              }}
                            />
                          }
                          label={
                            <Typography
                              variant="body2"
                              noWrap
                              title={category.name}
                            >
                              {category.name}
                            </Typography>
                          }
                          sx={{ flexGrow: 1, mr: 0 }}
                        />
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            )}

            {selectedCategories.length > 0 && (
              <Box mt={2} display="flex" alignItems="center">
                <Typography variant="body2" color="text.secondary" mr={1}>
                  {t(messages.Selected_Categories())}
                </Typography>
                <Chip
                  label={selectedCategories.length}
                  size="small"
                  color="primary"
                  sx={{
                    bgcolor: "#ff8038",
                    height: "22px",
                    "& .MuiChip-label": { px: 1 },
                  }}
                />
              </Box>
            )}

            {selectedCategories.length === 0 && selectedFile && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {t(messages.Please_Select_At_Least_One_Category())}
              </Alert>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid #e0e0e0",
          backgroundColor: "#f9f9f9",
        }}
      >
        <Button
          onClick={handleCloseModal}
          color="inherit"
          variant="outlined"
          sx={{ borderColor: "#d0d0d0" }}
          disabled={loading}
          data-testid="excel-import-cancel-button"
        >
          {t(messages.Cancel())}
        </Button>
        <Button
          onClick={handleImport}
          variant="contained"
          sx={{
            backgroundColor: "#ff8038",
            "&:hover": {
              backgroundColor: "#e06b2d",
            },
            minWidth: "120px",
          }}
          disabled={selectedCategories.length === 0 || !selectedFile || loading}
          startIcon={
            loading ? <CircularProgress size={20} color="inherit" /> : undefined
          }
          data-testid="excel-import-submit-button"
        >
          {loading ? t(messages.Importing()) : t(messages.Import())}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
